import type { APIError } from '@vizit/api';
import { BenchmarkDialog } from '@vizit/organisms';
import { type CreatedBenchmark, useBenchmarkNewDialog } from './benchmark-new-dialog.hooks';

interface BenchmarkNewDialogProps {
  /**
   * Workspace the benchmark is created in
   *
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Whether to hide the trigger button, e.g. when the Benchmarks view renders
   * its own "Create benchmark" button
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  hideTrigger?: boolean;
  /**
   * Called with the new benchmark's ID, name and image type once it is created
   *
   * @param benchmarkId - The ID of the created benchmark
   * @param benchmark - Its name and image type
   */
  onSuccessfulCreate: (benchmarkId: string, benchmark: CreatedBenchmark) => void;
  /**
   * Called when the API rejects the request as unauthenticated or unauthorized
   *
   * @param error - The error that occurred
   */
  onAuthenticationError: (error: APIError) => void;
  /**
   * Called when the dialog opens or closes
   *
   * @optional
   * @param isOpen - Whether the dialog is open
   */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * The "New benchmark" dialog wired to the API: renders BenchmarkDialog and
 * creates the benchmark on submit.
 *
 * @example
 * <BenchmarkNewDialog
 *   workspaceId="workspace-1"
 *   isOpen={isOpen}
 *   hideTrigger
 *   onOpenChange={setIsOpen}
 *   onSuccessfulCreate={(id) => navigate(id)}
 *   onAuthenticationError={onAuthenticationError}
 * />
 */
function BenchmarkNewDialog(props: BenchmarkNewDialogProps) {
  const { workspaceId, isOpen, hideTrigger = false, onSuccessfulCreate, onAuthenticationError, onOpenChange } = props;

  const { onSubmit } = useBenchmarkNewDialog({
    workspaceId,
    onSuccessfulCreate,
    onAuthenticationError,
  });

  return <BenchmarkDialog isOpen={isOpen} hideTrigger={hideTrigger} onSubmit={onSubmit} onOpenChange={onOpenChange} />;
}

export { BenchmarkNewDialog };
export type { BenchmarkNewDialogProps };
